import React, { useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Tooltip } from '@mui/material';
import _ from 'lodash';
import { ChevronLeft, CloudUpload } from 'lucide-react';
import * as z from 'zod';

import Button from '../../common/Button';
import { extractValue, toastPromise } from '../../common/utils';
import {
  useLeaveOrganization,
  useUpdateOrganization,
} from '../../hooks/tanstack/useOrganizationHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  isOrgGuest,
  isOrgSuperAdmin,
  useCustomNavigate,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import routes from '../../routes';
import cn from '../../utils/cn';
import {
  useCurrentMembership,
  useCurrentOrganization,
} from '../../utils/helpers';
import FormInput from '../form/FormInput';
import WarningDialog from '../WarningDialog';

const OrganizationInfoSchema = z.object({
  name: z
    .string()
    .min(1, {
      message: "Organization's name is required",
    })
    .trim(),
  description: z.string().optional(),
  profile_pic: z.custom<FileList>(),
});

type OrganizationInfoSchemaType = z.infer<typeof OrganizationInfoSchema>;

const OrganizationInfoForm = () => {
  const [imageSrc, setImageSrc] = useState<string | ArrayBuffer | null>(null);
  const { data: user } = useGetUser();
  const [isDragOver, setIsDragOver] = useState(false);

  const [error, setError] = useState<Record<string, string>>({});

  const { data: organization } = useCurrentOrganization();

  const isOwner = organization && isOrgSuperAdmin(organization.role);

  const { orgId } = useGetRouteParams();

  const form = useForm<OrganizationInfoSchemaType>({
    resolver: zodResolver(OrganizationInfoSchema),
  });

  const { handleSubmit, reset, formState, setValue } = form;

  const { isSubmitting, dirtyFields } = formState;

  const { mutateAsync: updateOrganizationInfo } = useUpdateOrganization({
    onSuccess() {
      reset();
    },
  });

  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name,
        description: organization.description,
      });

      if (organization.logo) {
        setImageSrc(organization.logo);
      }
    }
  }, [organization]);

  const onSubmit: SubmitHandler<OrganizationInfoSchemaType> = (data) => {
    const currentFileName = user?.profile_pic?.split('/').pop();

    if (!user || !organization) {
      return;
    }

    if (
      _.isEmpty(dirtyFields) &&
      currentFileName === (imageSrc as string)?.split('/').pop()
    ) {
      return;
    }

    const res = updateOrganizationInfo({
      orgId,
      data: {
        name: data.name,
        description: data.description,
        logo: data.profile_pic?.[0] as File,
      },
    });

    toastPromise({
      promise: res,
      content: 'Updated organization info successfully.',
    });
  };

  const onDrop = () => {
    setIsDragOver(false);
  };

  const onDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleUploadImage = (e: { target: { files: any } }) => {
    const formData = new FormData();

    Array.from(e.target.files as File[]).forEach((file: File) => {
      formData.append('avatar', file);
    });

    setValue('profile_pic', e.target.files);

    const reader = new FileReader();

    reader.onloadend = () => {
      setImageSrc(reader.result);
    };

    reader.readAsDataURL(e.target.files?.[0]);
  };

  const errorMessage = extractValue(error);

  const isDisabled = !isOwner || isSubmitting;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 rounded-[4px] border border-[#E4E7EC] p-6"
      >
        <div className="flex flex-col">
          <h2 className="text-xl font-bold">Organization info</h2>
          <p className="text-[#666]">
            Update your organization logo and name here.
          </p>
        </div>

        <div className="grid grid-cols-5 pt-6">
          <label className="col-span-2 flex items-center font-semibold text-[#333333]">
            Name
          </label>
          <Tooltip
            title={!isOwner ? 'Only organization owner can edit this' : ''}
          >
            <div className="col-span-3 flex gap-3">
              <FormInput
                name="name"
                disabled={isDisabled}
                fullWidth
                placeholder={organization?.name}
              />
            </div>
          </Tooltip>
        </div>

        <div className="border-t rounded-full border-[#E4E7EC] h-[1px]" />

        <div className="grid grid-cols-5">
          <label className="col-span-2 flex items-center font-semibold text-[#333333]">
            Description
          </label>
          <Tooltip
            title={!isOwner ? 'Only organization owner can edit this' : ''}
          >
            <div className="col-span-3 flex gap-3">
              <FormInput
                name="description"
                rows={4}
                disabled={isDisabled}
                multiline
                fullWidth
                placeholder={organization?.description}
              />
            </div>
          </Tooltip>
        </div>

        <div className="border-t rounded-full border-[#E4E7EC] h-[1px]" />

        <div className="grid grid-cols-5 pt-6">
          <div className="col-span-2">
            <label className="flex items-center font-semibold text-[#333333]">
              Logo
            </label>
          </div>
          <div className="col-span-3 flex gap-6">
            <Avatar
              alt="avatar"
              src={imageSrc as string}
              sx={{
                height: 70,
                width: 70,
                rounded: 'full',
              }}
            />
            <Tooltip
              title={!isOwner ? 'Only organization owner can edit this' : ''}
            >
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDrop}
                className={cn(
                  'relative transition cursor-pointer flex w-full flex-col items-center gap-3 rounded-[4px] border border-[#CCC] py-6 text-[#666]',
                  {
                    'bg-[#F5F9FF] border-dashed border-[#2196F3] text-[#2196F3]':
                      isDragOver,
                    'opacity-40': isDisabled,
                  }
                )}
              >
                <input
                  type="file"
                  disabled={isDisabled}
                  accept=".png,.jpg"
                  onChange={handleUploadImage}
                  className="absolute opacity-0 h-full w-full"
                />
                <CloudUpload width={30} height={30} />
                <div className="space-y-1 text-center">
                  <p>
                    <span className="font-semibold text-primary-bright-blue">
                      Click to upload
                    </span>{' '}
                    or drag and drop
                  </p>
                  <p>PNG or JPG (max. 800x800px)</p>
                </div>
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-primary-red italic">
            {errorMessage ? `*${errorMessage}` : null}
          </p>
          {!isDisabled && (
            <Tooltip
              title={
                isDisabled
                  ? 'Only organization owner can proceed this action'
                  : ''
              }
            >
              <Button
                type={isDisabled ? 'button' : 'submit'}
                variant="contained_blue"
              >
                Update info
              </Button>
            </Tooltip>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

const OrganizationSettings = () => {
  const [openWarningDialog, setOpenWarningDialog] = useState(false);

  const { data: currentOrg } = useCurrentOrganization();

  const { orgId } = useGetRouteParams();

  const { organization } = useCurrentMembership();

  const navigate = useCustomNavigate();

  const { mutateAsync: leaveOrg } = useLeaveOrganization({
    onSuccess() {
      if (organization) {
        navigate(routes.home, {
          org_id: organization.id,
        });
        return;
      }
      navigate(routes.organization.invitations);
    },
  });

  const handleLeaveOrganization = () => {
    const res = leaveOrg({
      orgId,
    });

    toastPromise({
      promise: res,
      content: 'Left organization successfully',
    });
  };

  const handleGoBack = async () => {
    navigate('/' + routes.organization.viewAll, {
      org_id: orgId,
    });
  };

  const handleOpenWarningDialog = () => setOpenWarningDialog(true);

  const handleCloseWarningDialog = () => setOpenWarningDialog(false);

  const isOwner = organization && isOrgSuperAdmin(organization.role);
  const isGuest = organization && isOrgGuest(organization.role);

  return (
    <div className="flex mx-auto max-w-lg 3xl:max-w-2xl flex-col gap-6 py-6 pb-20">
      <div className="flex flex-col gap-2">
        <button onClick={handleGoBack} className="flex items-center gap-2">
          <ChevronLeft />
          <h1 className="text-[28px] font-bold">Organization Settings</h1>
        </button>
        <div className="flex flex-col gap-2 border border-[#E4E7EC] px-6 py-4">
          <p className="flex-grow font-bold">Description</p>
          {currentOrg?.description?.length ? (
            <p className="line-clamp-2">{currentOrg?.description}</p>
          ) : (
            <p className="text-gray-500">No description provided</p>
          )}
        </div>
      </div>
      <OrganizationInfoForm />
      <div className="flex justify-start">
        {!isGuest && (
          <Tooltip
            title={
              isOwner
                ? 'Owner has to transfer their title first in order to leave this organization'
                : ''
            }
          >
            <Button
              onClick={() => !isOwner && handleOpenWarningDialog()}
              variant="text"
              style={{
                color: isOwner ? '#666' : '#b8341b',
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              Leave organization
            </Button>
          </Tooltip>
        )}
      </div>
      {organization && (
        <WarningDialog
          open={openWarningDialog}
          primaryActionText="Ok"
          handlePrimaryAction={handleLeaveOrganization}
          secondaryActionText="Cancel"
          handleSecondaryAction={handleCloseWarningDialog}
          content={`Are you sure about leaving ${organization.name}. You wont be able to join back this organization, only get invited.`}
        />
      )}
    </div>
  );
};

export default OrganizationSettings;
